import { FieldTimeOutlined, UserOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  DatePicker,
  Flex,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  TableProps,
  Tag,
  Typography,
} from "antd";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { debounce } from "lodash";
import React from "react";
import { StepStatus } from "../../enum/common";

import { IOvertime, IOvertimeRequestQuery } from "../../models/Hrm";
import { IPaginatedResponse } from "../../models/Pagination";
import { useCancelOvertimeRequest } from "../../query/HrmQuery";
import { notify } from "../../utils/toast";
import { getStatusTag } from "../filter/FilterEnum";
import { getDisplayStatus } from "../filter/FilterUserOperation";

const { Text } = Typography;
interface props {
  data: IPaginatedResponse<IOvertime>;
  isLoading: boolean;
  isHasAvatars?: boolean;
  filter: IOvertimeRequestQuery;
  setFilter: React.Dispatch<React.SetStateAction<IOvertimeRequestQuery>>;
}

const PersonalOvertimeTable: React.FC<props> = ({
  data,
  filter,
  isHasAvatars = false,
  isLoading,
  setFilter,
}) => {
  const cancel = useCancelOvertimeRequest();

  const handleCancel = async (id: string) => {
    try {
      await cancel.mutateAsync({ id });
      notify.success("Hủy thành công");
    } catch (error: unknown) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || "Hủy thất bại";
      notify.error(message);
    }
  };

  const statusApproval = [
    { value: StepStatus.MANAGER_PENDING, label: "Chờ quản lý duyệt" },
    { value: StepStatus.HR_PENDING, label: "Chờ nhân sự duyệt" },
    { value: StepStatus.DIRECTOR_PENDING, label: "Chờ giám đốc duyệt" },
    { value: StepStatus.APPROVED, label: "Đã duyệt" },
    { value: StepStatus.REJECTED, label: "Từ chối" },
  ];

  const handleSearch =
    (field: keyof IOvertimeRequestQuery) => (value: string) => {
      debouncedSearch(field, value);
    };

  const debouncedSearch = React.useMemo(
    () =>
      debounce((field: keyof IOvertimeRequestQuery, value: string) => {
        setFilter((prev) => ({
          ...prev,
          [field]: value || undefined,
          page: 1,
        }));
      }, 300),
    [setFilter],
  );
  const columns: TableProps<IOvertime>["columns"] = [
    ...(isHasAvatars
      ? [
          {
            title: filter.keyword
              ? `Nhân viên (${filter.keyword})`
              : "Nhân viên",
            key: "employee",
            width: 250,
            render: (_: any, record: any) => (
              <Space size="small">
                <Avatar
                  icon={<UserOutlined />}
                  className="shrink-0 bg-blue-100 text-blue-600"
                  src={record.createdByUser?.avatar}
                />

                <Flex vertical>
                  <Text strong className="truncate text-sm">
                    {record.createdByUser?.fullName}
                  </Text>

                  <Text className="truncate text-xs">
                    {record.createdByUser?.email}
                  </Text>
                </Flex>
              </Space>
            ),
          },
        ]
      : []),
    {
      title:
        filter.hour !== undefined
          ? `Thời gian tăng ca (${filter.hour}h)`
          : "Thời gian tăng ca",
      key: "time",
      width: 200,
      filterDropdown: () => (
        <InputNumber
          min={0}
          max={24}
          value={filter.hour}
          placeholder="Nhập số giờ"
          onChange={(value) => {
            setFilter((prev) => ({
              ...prev,
              hour: value ?? undefined,
              page: 1,
            }));
          }}
          style={{ width: 120 }}
        />
      ),
      render: (_, record) => (
        <Tag
          icon={<FieldTimeOutlined />}
          className="px-2 py-1 rounded-xl font-bold"
          color="blue"
        >
          {record.hour}h {record.minute > 0 ? `${record.minute}m` : ""}
        </Tag>
      ),
    },
    {
      title: filter.status
        ? `Trạng thái (${getStatusTag(filter.status).label})`
        : "Trạng thái",
      width: 250,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <Select
          className="w-[150px]"
          placeholder="Chọn..."
          value={filter.status}
          onChange={(value) => handleSearch("status")(value)}
          allowClear
          options={statusApproval}
        />
      ),
      render: (_, record) => {
        const status = getDisplayStatus(record);
        return (
          <Tag
            color={status.color}
            className="px-2 py-1 rounded-xl font-semibold"
          >
            {status.label}
          </Tag>
        );
      },
    },
    {
      title: filter.efficeDate
        ? `Ngày đăng ký (${dayjs(filter.efficeDate).format("DD/MM/YYYY")})`
        : "Ngày đăng ký",
      dataIndex: "efficeDate",
      width: 200,
      filterDropdown: () => (
        <DatePicker
          placeholder="Chọn ngày..."
          value={filter.efficeDate ? dayjs(filter.efficeDate) : null}
          onChange={(date) => {
            setFilter((prev) => ({
              ...prev,
              efficeDate: date?.format("YYYY-MM-DD"),
            }));
          }}
        />
      ),
      render: (val) => dayjs(val).format("DD/MM/YYYY"),
    },
    {
      title: "Lý do",
      dataIndex: "detail",
      key: "detail",
      width: 150,
      ellipsis: true,
      className: "cursor-pointer",
    },
    {
      title: "Thao tác",
      key: "action",
      width: 200,
      align: "right",
      render: (_, record) => {
        const expiredDate = dayjs(record.efficeDate)
          .add(1, "month")
          .date(3)
          .endOf("day");

        const isExpired = dayjs().isAfter(expiredDate);

        if (isExpired) {
          return <Tag color="red">Hết hạn</Tag>;
        }

        const isCanCancel = record.status?.includes("PENDING");

        return (
          <Popconfirm
            title="Hủy đơn tăng ca?"
            description="Bạn có chắc muốn hủy đơn tăng ca này?"
            okText="Hủy đơn"
            cancelText="Đóng"
            onConfirm={() => handleCancel(record.id)}
            disabled={!isCanCancel}
          >
            <Button type="primary" disabled={!isCanCancel}>
              Hủy
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div className="flex-1  p-4 overflow-auto h-full">
      <Flex vertical gap="large" className="w-full">
        <Table
          scroll={{ x: 1000 }}
          columns={columns}
          dataSource={data?.data}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: data?.pagination.page,
            pageSize: data?.pagination.limit,
            total: data?.pagination.total,
            onChange: (page, pageSize) => {
              setFilter({
                ...filter,
                page: page,
                limit: pageSize,
              });
            },
          }}
          className="shadow-sm border overflow-hidden"
          onRow={(record) => ({
            className: "cursor-pointer hover:bg-blue-50/30 transition-colors",
          })}
        />
      </Flex>
    </div>
  );
};

export default PersonalOvertimeTable;
